import apiService from '../services/apiService';
import TeamMember from '../models/TeamMember';

export const teamMemberController = {
  // Get all team members with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `team-members?${queryParams}` : 'team-members';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => TeamMember.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch team members' };
    }
  },

  // Get single team member by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`team-members/${id}`);
      return { success: true, data: TeamMember.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get team members by project
  getByProject: async (projectId) => {
    try {
      const response = await apiService.get(`team-members?projectId=${projectId}`);
      const items = response.data?.map(item => TeamMember.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Get team members by employee
  getByEmployee: async (employeeId) => {
    try {
      const response = await apiService.get(`team-members?employeeId=${employeeId}`);
      const items = response.data?.map(item => TeamMember.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Create new team member
  create: async (data) => {
    try {
      const item = new TeamMember(data);
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.post('team-members', item.toJSON());
      return { 
        success: true, 
        data: TeamMember.fromJSON(response.data), 
        message: 'Team member added successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing team member
  update: async (id, data) => {
    try {
      const item = new TeamMember({ ...data, id });
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.put(`team-members/${id}`, item.toJSON());
      return { 
        success: true, 
        data: TeamMember.fromJSON(response.data), 
        message: 'Team member updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update team member status
  updateStatus: async (id, status) => {
    try {
      const updateData = { status };
      if (status === 'Inactive') {
        updateData.leftDate = new Date().toISOString().split('T')[0];
      }
      const response = await apiService.patch(`team-members/${id}/status`, updateData);
      return { 
        success: true, 
        data: TeamMember.fromJSON(response.data), 
        message: 'Status updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update allocation
  updateAllocation: async (id, allocation) => {
    try {
      const response = await apiService.patch(`team-members/${id}/allocation`, { allocation });
      return { 
        success: true, 
        data: TeamMember.fromJSON(response.data), 
        message: 'Allocation updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete team member
  delete: async (id) => {
    try {
      await apiService.delete(`team-members/${id}`);
      return { success: true, message: 'Team member removed successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk update team members
  bulkUpdate: async (memberIds, updates) => {
    try {
      const response = await apiService.post('team-members/bulk-update', { memberIds, updates });
      return { success: true, data: response.data, message: 'Team members updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk delete team members
  bulkDelete: async (memberIds) => {
    try {
      await apiService.post('team-members/bulk-delete', { memberIds });
      return { success: true, message: 'Team members removed successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Export team members
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`team-members/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get team statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `team-members/statistics?${queryParams}` : 'team-members/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  }
};

export default teamMemberController;